import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment } from 'src/environments/environment';
import { VentaValidadorUpdateEvent } from '../interfaces/websocket.interfaces';

/**
 * Servicio de WebSocket (Socket.IO) para recibir notificaciones en tiempo real
 * Incluye reconexión automática
 */
@Injectable({
  providedIn: 'root',
})
export class WebSocketService implements OnDestroy {
  private socket: Socket | null = null;
  private ventaUpdates$ = new Subject<VentaValidadorUpdateEvent>();

  /**
   * Conecta al servidor WebSocket si el usuario es validador
   */
  connect(): void {
    const esValidador = localStorage.getItem('validadorPreVenta') === 'true' || localStorage.getItem('validadorPostVenta') === 'true';

    if (!esValidador) {
      return;
    }

    const token = localStorage.getItem('accessToken');
    if (!token) {
      return;
    }

    if (this.socket?.connected) {
      return;
    }

    this.socket = io(this.buildWsUrl(), {
      path: this.buildWsPath(),
      auth: { token },
      query: { token },
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 3000,
    });

    this.setupEventHandlers();
  }

  /**
   * Desconecta del servidor WebSocket
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  /**
   * Observable para suscribirse a actualizaciones de validador en ventas
   */
  onVentaValidadorUpdate(): Observable<VentaValidadorUpdateEvent> {
    return this.ventaUpdates$.asObservable();
  }

  ngOnDestroy(): void {
    this.disconnect();
    this.ventaUpdates$.complete();
  }

  /** Origen del API (esquema, host y puerto), sin el prefijo. */
  private buildWsUrl(): string {
    return this.apiUrl().origin;
  }

  /**
   * Ruta del handshake de Socket.IO bajo el prefijo del API.
   * En el servidor conviven varias APIs y solo se distinguen por su prefijo,
   * por lo que el socket también debe viajar dentro de él.
   */
  private buildWsPath(): string {
    const prefix = this.apiUrl().pathname.replace(/\/+$/, '');
    return `${prefix}/socket.io`;
  }

  private apiUrl(): URL {
    return new URL(environment.apiVentas, window.location.origin);
  }

  private setupEventHandlers(): void {
    if (!this.socket) return;

    this.socket.on('venta_validador_update', (event: VentaValidadorUpdateEvent) => {
      this.ventaUpdates$.next(event);
    });

    this.socket.on('error', (error: any) => {
      console.error('WebSocket: Error', error);
    });

    this.socket.on('disconnect', (reason: string) => {
      console.log('WebSocket: Desconectado -', reason);
    });

    this.socket.on('connect_error', (error: Error) => {
      console.error('WebSocket: Error de conexión', error.message);
    });
  }
}
